import {
  DoubaoRequest,
  DoubaoResponse,
  DoubaoStreamResponse,
  AIConfig,
  ConversationContext,
  DoubaoMessage,
} from "./types";
import { SYSTEM_PROMPTS } from "./prompts";

export class DoubaoClient {
  private config: AIConfig;

  constructor(config: AIConfig) {
    this.config = config;
  }

  /**
   * 发送单次请求到 Doubao-Seed-1.6
   */
  async sendMessage(
    context: ConversationContext,
    userMessage: string
  ): Promise<DoubaoResponse> {
    // 检查对话轮次限制
    if (context.roundCount >= this.config.maxConversationRounds) {
      throw new Error(
        `对话轮次已达到最大限制 ${this.config.maxConversationRounds} 轮`
      );
    }

    const messages: DoubaoMessage[] = [
      { role: "system", content: SYSTEM_PROMPTS.VIRTUAL_PATIENT },
      ...context.messages,
      { role: "user", content: userMessage },
    ];

    const request: DoubaoRequest = {
      model: "doubao-seed-1.6-250615",
      messages,
      max_tokens: this.config.maxTokens,
      temperature: this.config.temperature,
      stream: false,
    };

    try {
      const response = await this.callDoubaoAPI(request);
      return response;
    } catch (error) {
      console.error("Doubao API 调用失败:", error);
      throw new Error("AI服务暂时不可用，请稍后重试");
    }
  }

  /**
   * 发送流式请求到 Doubao-Seed-1.6
   */
  async sendMessageStream(
    context: ConversationContext,
    userMessage: string,
    onChunk: (chunk: DoubaoStreamResponse) => void
  ): Promise<void> {
    // 检查对话轮次限制
    if (context.roundCount >= this.config.maxConversationRounds) {
      throw new Error(
        `对话轮次已达到最大限制 ${this.config.maxConversationRounds} 轮`
      );
    }

    const messages: DoubaoMessage[] = [
      { role: "system", content: SYSTEM_PROMPTS.VIRTUAL_PATIENT },
      ...context.messages,
      { role: "user", content: userMessage },
    ];

    const request: DoubaoRequest = {
      model: "doubao-seed-1.6-250615",
      messages,
      max_tokens: this.config.maxTokens,
      temperature: this.config.temperature,
      stream: true,
    };

    try {
      await this.callDoubaoStreamAPI(request, onChunk);
    } catch (error) {
      console.error("Doubao Stream API 调用失败:", error);
      throw new Error("AI服务暂时不可用，请稍后重试");
    }
  }

  /**
   * 真实API调用实现
   */
  private async callDoubaoAPI(request: DoubaoRequest): Promise<DoubaoResponse> {
    console.log("发送API请求:", {
      url: `${this.config.baseURL}/chat/completions`,
      model: "doubao-seed-1.6-250615",
      messagesCount: request.messages.length,
      hasApiKey: !!this.config.apiKey,
    });

    const response = await fetch(`${this.config.baseURL}/chat/completions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.config.apiKey}`,
      },
      body: JSON.stringify({
        model: "doubao-seed-1.6-250615",
        messages: request.messages,
        max_tokens: request.max_tokens,
        temperature: request.temperature,
        stream: false,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("API调用失败:", {
        status: response.status,
        statusText: response.statusText,
        errorText,
        url: `${this.config.baseURL}/chat/completions`,
      });
      throw new Error(`API调用失败: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log("API响应成功:", {
      id: data.id,
      model: data.model,
      choicesCount: data.choices?.length || 0,
    });
    return data as DoubaoResponse;
  }

  /**
   * 真实流式API调用实现
   */
  private async callDoubaoStreamAPI(
    request: DoubaoRequest,
    onChunk: (chunk: DoubaoStreamResponse) => void
  ): Promise<void> {
    const response = await fetch(`${this.config.baseURL}/chat/completions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.config.apiKey}`,
      },
      body: JSON.stringify({
        model: "doubao-seed-1.6-250615",
        messages: request.messages,
        max_tokens: request.max_tokens,
        temperature: request.temperature,
        stream: true,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("流式API调用失败:", response.status, errorText);
      throw new Error(
        `流式API调用失败: ${response.status} ${response.statusText}`
      );
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("无法获取响应流");
    }

    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith("data: ")) {
            const data = trimmedLine.slice(6);
            if (data === "[DONE]") {
              return;
            }

            try {
              const chunk = JSON.parse(data) as DoubaoStreamResponse;
              onChunk(chunk);
            } catch (e) {
              console.warn("解析流式响应失败:", e, data);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * 创建新的对话上下文
   */
  createContext(sessionId: string): ConversationContext {
    return {
      messages: [],
      roundCount: 0,
      sessionId,
    };
  }

  /**
   * 更新对话上下文
   */
  updateContext(
    context: ConversationContext,
    userMessage: string,
    assistantMessage: string
  ): ConversationContext {
    return {
      ...context,
      messages: [
        ...context.messages,
        { role: "user", content: userMessage },
        { role: "assistant", content: assistantMessage },
      ],
      roundCount: context.roundCount + 1,
    };
  }

  /**
   * 检查是否达到对话轮次限制
   */
  isContextLimitReached(context: ConversationContext): boolean {
    return context.roundCount >= this.config.maxConversationRounds;
  }
}
